"use client";

import { useEffect, useState, useRef } from "react";

interface StickySidebarProps {
  children: React.ReactNode;
}

export default function StickySidebar({ children }: StickySidebarProps) {
  const [topOffset, setTopOffset] = useState(80); // Default fallback
  const sidebarRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const calculateOffset = () => {
      // Find the MenuNavbar (sticky navbar with logo)
      // Try multiple selectors to ensure we find it
      let navbar: HTMLElement | null = document.querySelector('nav[class*="sticky"]') as HTMLElement;
      
      // If not found by class, try finding by position sticky
      if (!navbar) {
        const allNavs = Array.from(document.querySelectorAll('nav'));
        navbar = allNavs.find((nav) => {
          const styles = window.getComputedStyle(nav);
          return styles.position === 'sticky' && styles.top === '0px';
        }) as HTMLElement || null;
      }
      
      if (navbar) {
        // Get the actual height of the navbar
        // Use offsetHeight first as it's more reliable for sticky elements
        let navbarHeight = navbar.offsetHeight;
        
        // If offsetHeight is 0 or invalid, use getBoundingClientRect
        if (!navbarHeight || navbarHeight === 0) {
          const rect = navbar.getBoundingClientRect();
          navbarHeight = rect.height;
        }
        
        // Ensure we have a valid height
        if (navbarHeight && navbarHeight > 0) {
          // Add padding (16px = 1rem)
          const calculatedOffset = navbarHeight + 16;
          setTopOffset(calculatedOffset);
        } else {
          // Fallback if height calculation fails
          setTopOffset(80);
        }
      } else {
        // Fallback: approximate navbar height (~64px for MenuNavbar with py-3) + padding
        setTopOffset(80);
      }
    };

    // Calculate immediately
    calculateOffset();
    
    // Recalculate after DOM is ready (multiple timeouts to catch different render phases)
    const timeout1 = setTimeout(calculateOffset, 50);
    const timeout2 = setTimeout(calculateOffset, 200);
    const timeout3 = setTimeout(calculateOffset, 500);
    const timeout4 = setTimeout(calculateOffset, 1000);
    
    // Recalculate on resize
    window.addEventListener("resize", calculateOffset, { passive: true });
    
    // Recalculate on scroll (navbar might change height or position)
    const handleScroll = () => {
      requestAnimationFrame(calculateOffset);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("resize", calculateOffset);
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
      clearTimeout(timeout4);
    };
  }, []);

  return (
    <section
      ref={sidebarRef}
      className="sticky h-fit w-full self-start md:flex md:w-1/2 md:flex-col"
      style={{
        top: `${topOffset}px`,
        alignSelf: "flex-start",
      }}
    >
      {children}
    </section>
  );
}
